import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import type { MonitorIR, MonitorStatus, MatchStage } from "./schemas";
import { MonitorIRSchema } from "./schemas";

// -----------------------------------------------------------------------------
// Response shapes — narrowed from src/api/generated.ts where stable.
// Keep these in sync with the backend OpenAPI; regenerate generated.ts whenever
// the backend changes.
// -----------------------------------------------------------------------------
export interface MonitorListItem {
  id: string;
  statement: string;
  canonical_statement: string;
  status: MonitorStatus;
  last_fired_at: string | null;
  // Audit I3: backend records 'volume_cap' | 'consecutive_wrong' when the
  // poll worker auto-pauses; null when never auto-paused or after first
  // manual pause that pre-dated the column. UI reads this on AutoPausedBanner.
  last_pause_reason: string | null;
  created_at: string;
}

export interface MonitorDetail extends MonitorListItem {
  ir: MonitorIR;
  candidate_threshold: number;
  verify_threshold: number;
  confirm_threshold: number;
  cooldown_seconds: number;
  max_matches_per_day: number | null;
  updated_at: string;
}

export interface MonitorMatch {
  id: string;
  monitor_id?: string;
  cluster_id: string;
  cluster_snapshot: {
    title?: string;
    summary?: string;
    categories?: string[];
    materials?: string[];
  };
  stage: MatchStage;
  similarity_score: number;
  confidence: number | null;
  verifier_reason: string | null;
  detected_at: string;
  delivered_at: string | null;
  user_feedback: "correct" | "wrong" | null;
}

// -----------------------------------------------------------------------------
// Query keys
// -----------------------------------------------------------------------------
export const MONITORS_KEYS = {
  list: ["monitors", "list"] as const,
  detail: (id: string) => ["monitors", "detail", id] as const,
  matches: (monitorId: string, params: { stage?: MatchStage; limit?: number }) =>
    ["monitors", "matches", { monitorId, ...params }] as const,
  inbox: (params: { stage?: MatchStage; limit?: number } = {}) =>
    ["monitors", "inbox", params] as const,
  unread: ["monitors", "inbox-unread-count"] as const,
  draft: (id: string) => ["monitors", "draft", id] as const,
};

// -----------------------------------------------------------------------------
// Read hooks
// -----------------------------------------------------------------------------
export function useMonitorsList(): UseQueryResult<{ monitors: MonitorListItem[] }> {
  return useQuery({
    queryKey: MONITORS_KEYS.list,
    queryFn: () => apiFetch<{ monitors: MonitorListItem[] }>("/api/v1/monitors"),
  });
}

export function useMonitor(id: string): UseQueryResult<MonitorDetail> {
  return useQuery({
    queryKey: MONITORS_KEYS.detail(id),
    queryFn: async (): Promise<MonitorDetail> => {
      const raw = await apiFetch<MonitorDetail>(`/api/v1/monitors/${id}`);
      // Parse the IR through zod so a backend shape drift (renamed/missing
      // required field) surfaces as a query error instead of a half-broken
      // detail page. Defaults + optionals make additive changes a no-op.
      return { ...raw, ir: MonitorIRSchema.parse(raw.ir) };
    },
    enabled: Boolean(id),
  });
}

export function useMonitorMatches(
  monitorId: string,
  params: { stage?: MatchStage; limit?: number } = {},
): UseQueryResult<{ matches: MonitorMatch[] }> {
  const qs = new URLSearchParams();
  if (params.stage) qs.set("stage", params.stage);
  if (params.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return useQuery({
    queryKey: MONITORS_KEYS.matches(monitorId, params),
    queryFn: () =>
      apiFetch<{ matches: MonitorMatch[] }>(
        `/api/v1/monitors/${monitorId}/matches${query ? `?${query}` : ""}`,
      ),
    enabled: Boolean(monitorId),
  });
}

export function useInbox(
  params: { stage?: MatchStage; limit?: number } = { stage: "confirmed" },
): UseQueryResult<{ matches: MonitorMatch[] }> {
  const qs = new URLSearchParams();
  if (params.stage) qs.set("stage", params.stage);
  if (params.limit) qs.set("limit", String(params.limit));
  return useQuery({
    queryKey: MONITORS_KEYS.inbox(params),
    queryFn: () =>
      apiFetch<{ matches: MonitorMatch[] }>(
        `/api/v1/monitors/matches${qs.toString() ? `?${qs}` : ""}`,
      ),
  });
}

export function useInboxUnreadCount(): UseQueryResult<number> {
  // Audit I2: dedicated server-side count, no longer the limit=500 trick
  // that truncated silently above that threshold.
  return useQuery({
    queryKey: MONITORS_KEYS.unread,
    queryFn: async () => {
      const res = await apiFetch<{ count: number }>(
        "/api/v1/monitors/unread-count",
      );
      return res.count;
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

// Audit I8: single source of truth for category + material chip pickers.
// Previously the wizard and detail page hardcoded their own lists, which
// would silently drift from backend if anyone added a 9th category. The
// `staleTime: Infinity` is acceptable because options change on backend
// deploys, which restart the SPA via cache-busting build hashes.
export interface MonitorOptions {
  categories: string[];
  materials: string[];
}

export function useMonitorOptions(): UseQueryResult<MonitorOptions> {
  return useQuery({
    queryKey: ["monitors", "options"],
    queryFn: () => apiFetch<MonitorOptions>("/api/v1/monitors/options"),
    staleTime: Infinity,
  });
}

// Audit I4: read a draft back from the backend so the wizard can recover
// its in-memory state after a page reload at /monitors/new?step=2&draft_id=…
// Returns the same CompileResult shape POST /monitors/draft returns, plus
// `created_at` (the wizard uses it to show a "draft expires in N min" hint
// if we want — out of scope for the audit fix itself).
export interface DraftResponse extends CompileResult {
  created_at: string;
}

export function useDraft(draftId: string | undefined): UseQueryResult<DraftResponse> {
  return useQuery({
    queryKey: ["monitors", "draft", draftId],
    queryFn: async (): Promise<DraftResponse> => {
      const raw = await apiFetch<DraftResponse>(`/api/v1/monitors/draft/${draftId}`);
      return { ...raw, ir: MonitorIRSchema.parse(raw.ir) };
    },
    enabled: Boolean(draftId),
    // Drafts are 1h-TTL'd on the backend; one fetch per wizard mount is fine.
    staleTime: 60_000,
    retry: false,
  });
}

// -----------------------------------------------------------------------------
// Mutation payload / response shapes
// -----------------------------------------------------------------------------
export interface CompileResult {
  draft_id: string;
  canonical_statement: string;
  ir: MonitorIR;
  suggested_candidate_threshold: number;
}

export interface BacktestResult {
  would_have_confirmed: Array<{
    cluster_id: string;
    title: string;
    confidence: number;
    reason: string;
    first_seen_at: string;
  }>;
  candidates_only: Array<{ cluster_id: string; similarity: number }>;
  projected_rate_per_day: number;
  backtest_horizon?: string;
}

export interface CommitRequest {
  draft_id: string;
  candidate_threshold?: number;
  verify_threshold?: number;
  confirm_threshold?: number;
  cooldown_seconds?: number;
  max_matches_per_day?: number | null;
  ir_overrides?: Partial<MonitorIR>;
}

export interface PatchRequest {
  statement?: string;
  ir?: MonitorIR;
  candidate_threshold?: number;
  verify_threshold?: number;
  confirm_threshold?: number;
  cooldown_seconds?: number;
  max_matches_per_day?: number | null;
}

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------
// NOTE: project convention (see src/api/client.ts, src/contact/api.ts) is to
// serialize JSON bodies with JSON.stringify before handing them to apiFetch.
// apiFetch types `init` as RequestInit, so we keep that convention here and
// in the tests.

export function useCreateDraft(): UseMutationResult<
  CompileResult,
  Error,
  { statement: string }
> {
  return useMutation({
    mutationFn: ({ statement }) =>
      apiFetch<CompileResult>("/api/v1/monitors/draft", {
        method: "POST",
        body: JSON.stringify({ statement }),
      }),
  });
}

export function useRunBacktest(): UseMutationResult<
  BacktestResult,
  Error,
  { draftId: string }
> {
  return useMutation({
    mutationFn: ({ draftId }) =>
      apiFetch<BacktestResult>(`/api/v1/monitors/draft/${draftId}/backtest`, {
        method: "POST",
      }),
  });
}

export function useCommitMonitor(): UseMutationResult<
  { id: string },
  Error,
  CommitRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<{ id: string }>("/api/v1/monitors", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.list });
    },
  });
}

export function usePatchMonitor(): UseMutationResult<
  { id: string; status: string },
  Error,
  { id: string; patch: PatchRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) =>
      apiFetch<{ id: string; status: string }>(`/api/v1/monitors/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.list });
    },
  });
}

function useLifecycleMutation(action: "pause" | "resume" | "archive") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ status: string }>(`/api/v1/monitors/${id}/${action}`, {
        method: "POST",
      }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.list });
    },
  });
}

export function usePauseMonitor() {
  return useLifecycleMutation("pause");
}
export function useResumeMonitor() {
  return useLifecycleMutation("resume");
}
export function useArchiveMonitor() {
  return useLifecycleMutation("archive");
}

export function useDeleteMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/monitors/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: MONITORS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.list });
    },
  });
}

export function useAckMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) =>
      apiFetch<{ acked: boolean }>(
        `/api/v1/monitors/matches/${matchId}/ack`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.unread });
      qc.invalidateQueries({ queryKey: ["monitors", "inbox"] });
    },
  });
}

/**
 * Acks a batch of matches: fires N parallel POSTs but invalidates the inbox
 * and unread-count queries exactly once after the batch settles. Use this
 * when many matches need to be acked at the same time (e.g. the bell
 * dropdown opens with 10 unread items) instead of calling useAckMatch in a
 * loop, which triggers N redundant invalidations.
 *
 * Optimistically zeros the unread-count badge so the UI updates instantly.
 */
export function useAckMatchesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (matchIds: string[]) => {
      await Promise.allSettled(
        matchIds.map((id) =>
          apiFetch<{ acked: boolean }>(
            `/api/v1/monitors/matches/${id}/ack`,
            { method: "POST" },
          ),
        ),
      );
    },
    onMutate: () => {
      qc.setQueryData<number>(MONITORS_KEYS.unread, 0);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.unread });
      qc.invalidateQueries({ queryKey: ["monitors", "inbox"] });
    },
  });
}

export function useRecordFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      matchId,
      value,
    }: {
      matchId: string;
      value: "correct" | "wrong";
    }) =>
      apiFetch<{ value: string }>(
        `/api/v1/monitors/matches/${matchId}/feedback`,
        { method: "POST", body: JSON.stringify({ value }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monitors", "matches"] });
    },
  });
}
